import {ComponentProps, ComponentPropsWithoutRef, HTMLAttributes, ReactNode, useCallback} from 'react';
import {
  Combobox as HeadlessCombobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';
import clsx from 'clsx';
import {useTranslation} from 'react-i18next';
import {twMerge} from 'tailwind-merge';
import ArrowIcon from '@/assets/images/icon-arrow.svg?react';
import CheckIcon from '@/assets/images/icon-check.svg?react';
import CloseIcon from '@/assets/images/icon-close.svg?react';
import IconSearch from '@/assets/images/icon-search.svg?react';
import {useFormControlContext} from '@/components/form/form-control/form-control';
import Spinner from '@/components/spinner';

interface ComboboxValue {
  id: string;
  label: string;
  value: string;
}

type HeadlessComboboxProps = ComponentPropsWithoutRef<typeof HeadlessCombobox<ComboboxValue>>;
type HeadlessComboboxInputProps = ComponentProps<typeof ComboboxInput<ComboboxValue>>;

interface ComboboxContentProps<T extends ComboboxValue = ComboboxValue>
  extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  ['data-testid']?: string;
  selectedValue: T | null;
  values: T[];
  multiple?: HeadlessComboboxProps['multiple'];
  placeholder?: HeadlessComboboxInputProps['placeholder'];
  isLoading?: boolean;
  isReadonly?: boolean;
  showEmptyResults?: boolean;
  showMenuToggle?: boolean;
  setQuery: (query: string) => void;
  onChange: ((value: NoInfer<T> | null) => void) | undefined;
  onBlur?: () => void;
  onFocus?: () => void;
}

const ComboboxContent = <T extends ComboboxValue>({
  ['data-testid']: dataTestId,
  selectedValue = null,
  defaultValue: _,
  values,
  placeholder,
  multiple = false,
  isLoading = false,
  isReadonly = false,
  showEmptyResults = false,
  showMenuToggle = true,
  onChange,
  onFocus,
  onBlur,
  setQuery,
  ...props
}: ComboboxContentProps<T>): ReactNode | Promise<ReactNode> => {
  const {t} = useTranslation();

  const {isInError, isDisabled, hasWarning} = useFormControlContext();

  const onClose = useCallback(() => {
    setQuery('');
  }, [setQuery]);

  const onInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const {value} = event.target;
      setQuery(value);
    },
    [setQuery],
  );

  const onReset = useCallback(() => {
    setQuery('');
    onChange?.(null);
  }, [onChange, setQuery]);

  return (
    <HeadlessCombobox
      as="div"
      className="relative w-full"
      multiple={multiple}
      value={selectedValue}
      onChange={onChange}
      onClose={onClose}
      disabled={isDisabled || isReadonly}
      {...props}
    >
      <div className="group">
        <ComboboxInput
          data-testid={dataTestId}
          placeholder={placeholder}
          className={twMerge(
            clsx(
              'peer w-full appearance-none rounded-1 border border-violet bg-grey-100 p-4 pl-10 text-base text-violet placeholder-violet outline-none transition-all',
              !(isDisabled || isReadonly) &&
                'group-focus-within:placeholder-charcoal group-hover:shadow-input group-focus:border-violet group-focus:text-violet group-focus:shadow-inputFocus',
              isInError &&
                'border-red text-red placeholder-red caret-red group-hover:text-red group-hover:shadow-inputError group-focus:border-red group-focus:text-red group-focus:shadow-inputError',
              hasWarning &&
                'border-yellow-600 text-yellow-600 placeholder-yellow-600 caret-yellow-600 group-hover:text-yellow-600 group-hover:shadow-inputError group-focus:border-yellow-600 group-focus:text-yellow-600 group-focus:shadow-inputError',
              isDisabled && 'border-grey-400 bg-white text-grey-400 placeholder-grey-400 focus:border-grey-400',
              isReadonly &&
                'cursor-not-allowed border-grey-650 bg-white text-grey-650 placeholder-grey-650 shadow shadow-grey-400 hover:shadow-grey-400 focus:border-grey-400 focus:text-grey-650 focus:placeholder-grey-650',
            ),
          )}
          displayValue={(value: ComboboxValue) => value?.label}
          disabled={isDisabled || isReadonly}
          onChange={onInputChange}
          onFocus={onFocus}
          onBlur={onBlur}
        />

        <div className={clsx('absolute left-0 top-0 flex h-full w-fit items-center justify-end pl-4')}>
          <IconSearch
            className={twMerge(
              clsx(
                'size-3.5',
                'stroke-violet',
                isInError && 'stroke-red',
                (isDisabled || isReadonly) && 'stroke-grey-400',
                hasWarning && 'stroke-yellow-600',
              ),
            )}
          />
        </div>

        <div
          className={twMerge(
            clsx(
              'group/open-menu-button',
              'absolute right-px top-px flex h-[calc(100%-2px)] items-center justify-end overflow-hidden rounded-r-1 pl-8',
              !(isDisabled || isReadonly) && 'bg-gradient-to-l from-grey-100 via-grey-100 via-90%',
            ),
          )}
        >
          {isLoading && (
            <div
              data-testid={`${dataTestId}-loading-indicator`}
              className={clsx(
                'flex h-full items-center justify-end gap-2 text-violet',
                'pr-4',
                'group-has-[button]/open-menu-button:pr-0',
              )}
            >
              {t('components.combobox.loading')}
              <Spinner className="size-5" />
            </div>
          )}

          {showMenuToggle ? (
            <ComboboxButton
              className={clsx(
                'group/open-menu-button flex h-full items-center justify-end px-4',
                !(isDisabled || isReadonly) && 'hover:opacity-50',
              )}
              onFocus={onFocus}
              onBlur={onBlur}
              role="button"
            >
              <ArrowIcon
                className={twMerge(
                  clsx(
                    'size-3',
                    'transition-transform group-data-[open]/open-menu-button:-scale-100',
                    'fill-violet',
                    isInError && 'fill-red',
                    (isDisabled || isReadonly) && 'fill-grey-400',
                    hasWarning && 'fill-yellow-600',
                  ),
                )}
              />
            </ComboboxButton>
          ) : (
            selectedValue &&
            !(isDisabled || isReadonly) && (
              <ComboboxButton
                className={clsx('group/open-menu-button flex h-full items-center justify-end px-4 hover:opacity-50')}
                onClick={onReset}
              >
                <CloseIcon
                  className={twMerge(
                    clsx(
                      'size-2.5',
                      'fill-violet',
                      isInError && 'fill-red',
                      isDisabled && 'fill-grey-400',
                      hasWarning && 'fill-yellow-600',
                    ),
                  )}
                />
              </ComboboxButton>
            )
          )}
        </div>
      </div>

      <LocalComboboxOptions data-testid={dataTestId} values={values} showEmptyResults={showEmptyResults} />
    </HeadlessCombobox>
  );
};

type LocalComboboxOptionsProps = ComponentProps<typeof ComboboxOptions> &
  Pick<ComboboxContentProps, 'data-testid' | 'values' | 'showEmptyResults'> & {};

const LocalComboboxOptions: React.FC<LocalComboboxOptionsProps> = ({
  'data-testid': dataTestId,
  values,
  showEmptyResults,
  ...props
}) => (
  <ComboboxOptions
    {...props}
    anchor="bottom start"
    className="relative z-10 mt-1 max-h-4 w-[var(--input-width)] rounded-1 border border-greige-925 bg-white shadow-combobox transition duration-100 ease-in empty:invisible data-[leave]:data-[closed]:opacity-0"
    data-testid="combobox-options"
  >
    {showEmptyResults ? (
      <LocalComboboxOption data-testid={`${dataTestId}-option-no-results`} disabled value={null} />
    ) : (
      values.map(value => (
        <LocalComboboxOption key={value.id} data-testid={`${dataTestId}-option-${value.id}`} value={value} />
      ))
    )}
  </ComboboxOptions>
);

type LocalComboboxOptionProps = ComponentProps<typeof ComboboxOption> & {
  value: ComboboxValue | null;
  isSelectedValue?: boolean;
};

const LocalComboboxOption: React.FC<LocalComboboxOptionProps> = ({value, isSelectedValue, ...props}) => {
  const {t} = useTranslation();

  return (
    <ComboboxOption
      {...props}
      className={clsx(
        'flex select-none items-center gap-2 py-3 pl-4 text-black/60 data-[focus]:cursor-pointer data-[focus]:bg-grey-100',
      )}
      value={value}
    >
      {!value ? (
        <>{t('components.combobox.no-results')}</>
      ) : (
        <>
          <CheckIcon
            className={clsx(
              'size-4 pr-0.5 opacity-0',
              'fill-transparent',
              isSelectedValue && 'fill-violet opacity-100',
            )}
          />
          {value.label}
        </>
      )}
    </ComboboxOption>
  );
};

export {type ComboboxValue, type ComboboxContentProps, ComboboxContent as default};
